import { Injectable, signal, computed } from '@angular/core';

/**
 * Organization Context Service
 *
 * Manages the current organization context for the user session.
 * The organization ID is used to scope API requests via the X-Organization-Id header.
 *
 * Features:
 * - Signal-based reactive state management
 * - LocalStorage persistence across page refreshes
 * - Auto-initialization from localStorage on service creation
 *
 * Reference: /agent-os/specs/2026-01-07-organization-context-middleware/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationContextService {
  private static readonly STORAGE_KEY = 'adn_current_organization_id';

  // Private writable signal
  private _currentOrganizationId = signal<string | null>(null);

  // Public readonly computed signal
  public readonly currentOrganizationId = computed(() => this._currentOrganizationId());

  constructor() {
    // Initialize from localStorage on service creation
    this.loadFromStorage();
  }

  /**
   * Set the current organization context
   * Persists to localStorage and updates signal
   */
  setCurrentOrganization(organizationId: string): void {
    this._currentOrganizationId.set(organizationId);
    localStorage.setItem(OrganizationContextService.STORAGE_KEY, organizationId);
  }

  /**
   * Clear the current organization context
   * Removes from localStorage and clears signal
   */
  clearCurrentOrganization(): void {
    this._currentOrganizationId.set(null);
    localStorage.removeItem(OrganizationContextService.STORAGE_KEY);
  }

  /**
   * Get current organization ID (for use in interceptors)
   * Returns the current value directly (not a signal)
   */
  getCurrentOrganizationId(): string | null {
    return this._currentOrganizationId();
  }

  /**
   * Load organization ID from localStorage
   * Called on service initialization
   */
  private loadFromStorage(): void {
    const storedId = localStorage.getItem(OrganizationContextService.STORAGE_KEY);
    if (storedId) {
      this._currentOrganizationId.set(storedId);
    }
  }
}
